import { useEffect, useRef, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, type StateSnapshot } from "@touchscreen/api-client";
import { computeNextPollDelayMs, createBackoffState } from "@touchscreen/live-state";
import { useTouchscreenContext } from "./TouchscreenProvider.js";
import { touchscreenKeys } from "./queryKeys.js";

/**
 * Drives `GET /state` on the track-end-predicted schedule, via `refetchInterval` — this replaces
 * the old hand-rolled `PollEngine`. A kiosk polls forever (Studio derives "kiosk last seen" from
 * it); a phone pauses while the tab is hidden, per `surface` on the provider, then resumes with
 * an immediate fetch on `visibilitychange` back to visible.
 */
export function useVenueStateQuery(): UseQueryResult<StateSnapshot, ApiError> {
  const { client, surface, venue, boot } = useTouchscreenContext();
  const venueRef = venue?.ref ?? null;

  // The `since_version` cursor and the last snapshot live outside React state — they only feed
  // the next `queryFn` call and the "still showing the same thing" 304 case, never a render.
  const sinceVersionRef = useRef<number | undefined>(undefined);
  const lastSnapshotRef = useRef<StateSnapshot | null>(null);
  const backoffRef = useRef(createBackoffState());

  const pauseWhenHidden = surface === "phone";
  const [hidden, setHidden] = useState(pauseWhenHidden && typeof document !== "undefined" ? document.hidden : false);

  useEffect(() => {
    if (!pauseWhenHidden || typeof document === "undefined") return;
    const onVisibilityChange = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [pauseWhenHidden]);

  // A fresh venue means a fresh cursor — otherwise the first call for the new venue would send a
  // stale `since_version` from whatever venue was showing before.
  useEffect(() => {
    sinceVersionRef.current = undefined;
    lastSnapshotRef.current = null;
    backoffRef.current = createBackoffState();
  }, [venueRef]);

  return useQuery({
    queryKey: touchscreenKeys.state(venueRef ?? ""),
    enabled: boot.status === "ready" && !!venueRef && !(pauseWhenHidden && hidden),
    queryFn: async (): Promise<StateSnapshot> => {
      const result = await client.getState(sinceVersionRef.current);
      if (result.changed) {
        sinceVersionRef.current = result.snapshot.version;
        lastSnapshotRef.current = result.snapshot;
        return result.snapshot;
      }
      // Unchanged (304) — whatever we were last showing still holds.
      if (lastSnapshotRef.current) return lastSnapshotRef.current;
      throw new ApiError({ code: "unknown", status: 304, message: "Unexpected unchanged /state before a first snapshot." });
    },
    refetchInterval: (query) => computeNextPollDelayMs(query.state.data ?? null, query.state.status === "error", backoffRef.current),
    refetchIntervalInBackground: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: Infinity,
    retry: false,
  });
}
